"use client";

import { StatCard, StatGrid } from "@/components/tool-kit/primitives";
import { HINT, WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import type { ANOVA_CALCULATOR_STRINGS } from "@/components/tools/anova-calculator";
import {
  ApaReport,
  Decision,
  ResultTable,
  Working,
} from "@/components/tools/stats-tests/kit";
import { t } from "@/lib/i18n/interpolate";
import {
  leveneMedian,
  type OneWayAnova,
  pairwiseWelch,
  welchAnova,
} from "@/lib/tool-kit/stats-tests/anova";
import {
  apaP,
  fixed,
  formatDf,
  formatP,
  formatStat,
  type NumberFormatter,
  richTemplate,
} from "@/lib/tool-kit/stats-tests/format";

/** Results for the one-way ANOVA calculator. */

type Strings = typeof ANOVA_CALCULATOR_STRINGS;

function Assumptions({
  data,
  alpha,
  strings,
  fmt,
}: {
  data: readonly (readonly number[])[];
  alpha: number;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const levene = leveneMedian(data);
  const welch = welchAnova(data);
  const fill = (test: { f: number; df1: number; df2: number; p: number }) => ({
    df1: formatDf(test.df1, fmt),
    df2: formatDf(test.df2, fmt),
    f: formatStat(test.f, fmt),
    p: formatP(test.p, fmt),
  });
  let leveneVerdict: string = strings.leveneUndefined;
  if (levene) {
    leveneVerdict =
      levene.p < alpha ? strings.leveneUnequal : strings.leveneEqual;
  }
  return (
    <div className="space-y-2">
      <h3 className={WIDGET_H3}>{strings.assumptionsHeading}</h3>
      {levene ? (
        <p className="text-foreground text-sm">
          {t(strings.levene, fill(levene))}
        </p>
      ) : null}
      <p className={HINT}>{leveneVerdict}</p>
      <p className="text-foreground text-sm">
        {welch ? t(strings.welch, fill(welch)) : strings.welchUndefined}
      </p>
    </div>
  );
}

function PostHoc({
  data,
  names,
  strings,
  fmt,
}: {
  data: readonly (readonly number[])[];
  names: readonly string[];
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const pairs = pairwiseWelch(data);
  const n = (v: number) => formatStat(v, fmt);
  return (
    <div className="space-y-2">
      <h3 className={WIDGET_H3}>{strings.posthocHeading}</h3>
      <p className={HINT}>{t(strings.posthocHint, { m: fmt(pairs.length) })}</p>
      <ResultTable
        head={[
          strings.comparison,
          strings.difference,
          strings.t,
          strings.df,
          strings.p,
          strings.pAdjusted,
        ]}
        rows={pairs.map((pair) => ({
          key: `${pair.first}-${pair.second}`,
          cells: [
            t(strings.versus, { a: names[pair.first], b: names[pair.second] }),
            n(pair.difference),
            n(pair.t),
            formatStat(pair.df, fmt, 2),
            formatP(pair.p, fmt),
            formatP(pair.pAdjusted, fmt),
          ],
        }))}
      />
    </div>
  );
}

export function AnovaResults({
  r,
  data,
  names,
  alpha,
  strings,
  fmt,
}: {
  r: OneWayAnova;
  data: readonly (readonly number[])[];
  names: readonly string[];
  alpha: number;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const n = (v: number) => formatStat(v, fmt);
  const apa = richTemplate(strings.apaTemplate, {
    df1: fmt(r.df1),
    df2: fmt(r.df2),
    f: fixed(r.f, 2, fmt),
    p: apaP(r.p, fmt),
    eta: fixed(r.etaSquared, 2, fmt, false),
  });
  const terms = r.groups
    .map((g) => `${fmt(g.n)}(${n(g.mean)} − ${n(r.grandMean)})²`)
    .join(" + ");
  return (
    <section aria-live="polite" className="space-y-4">
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      <StatGrid>
        <StatCard emphasis label={strings.fStatistic} value={n(r.f)} />
        <StatCard
          label={strings.dfLabel}
          value={`${fmt(r.df1)}, ${fmt(r.df2)}`}
        />
        <StatCard emphasis label={strings.pValue} value={formatP(r.p, fmt)} />
        <StatCard label={strings.etaSquared} value={n(r.etaSquared)} />
        <StatCard label={strings.omegaSquared} value={n(r.omegaSquared)} />
        <StatCard label={strings.totalN} value={fmt(r.n)} />
      </StatGrid>
      <Decision reject={r.p < alpha}>
        {t(r.p < alpha ? strings.decisionReject : strings.decisionKeep, {
          alpha: fmt(alpha),
          p: formatP(r.p, fmt),
        })}
      </Decision>
      <h3 className={WIDGET_H3}>{strings.tableHeading}</h3>
      <ResultTable
        head={[
          strings.source,
          strings.ss,
          strings.df,
          strings.ms,
          strings.f,
          strings.p,
        ]}
        rows={[
          {
            key: "between",
            cells: [
              strings.between,
              n(r.ssBetween),
              fmt(r.df1),
              n(r.msBetween),
              n(r.f),
              formatP(r.p, fmt),
            ],
          },
          {
            key: "within",
            cells: [
              strings.within,
              n(r.ssWithin),
              fmt(r.df2),
              n(r.msWithin),
              "",
              "",
            ],
          },
          {
            key: "total",
            cells: [strings.total, n(r.ssTotal), fmt(r.n - 1), "", "", ""],
          },
        ]}
      />
      <h3 className={WIDGET_H3}>{strings.descriptivesHeading}</h3>
      <ResultTable
        head={[
          strings.group,
          strings.n,
          strings.mean,
          strings.sd,
          strings.median,
        ]}
        rows={r.groups.map((g, i) => ({
          key: `${i}-${names[i]}`,
          cells: [names[i], fmt(g.n), n(g.mean), n(g.sd), n(g.median)],
        }))}
      />
      <Assumptions alpha={alpha} data={data} fmt={fmt} strings={strings} />
      <PostHoc data={data} fmt={fmt} names={names} strings={strings} />
      <Working heading={strings.howCalculated}>
        <p>
          x̄ = {n(r.grandMean)} (N = {fmt(r.n)}, k = {fmt(r.groups.length)})
        </p>
        <p>
          SS<sub>B</sub> = Σ nⱼ(x̄ⱼ − x̄)² = {terms} = {n(r.ssBetween)}
        </p>
        <p>
          SS<sub>W</sub> = Σ Σ (x − x̄ⱼ)² = {n(r.ssWithin)}
        </p>
        <p>
          MS<sub>B</sub> = SS<sub>B</sub> / (k − 1) = {n(r.ssBetween)} /{" "}
          {fmt(r.df1)} = {n(r.msBetween)}; MS<sub>W</sub> = SS<sub>W</sub> / (N
          − k) = {n(r.ssWithin)} / {fmt(r.df2)} = {n(r.msWithin)}
        </p>
        <p>
          F = MS<sub>B</sub> / MS<sub>W</sub> = {n(r.msBetween)} /{" "}
          {n(r.msWithin)} = {n(r.f)}
        </p>
        <p>
          η² = SS<sub>B</sub> / SS<sub>T</sub> = {n(r.ssBetween)} /{" "}
          {n(r.ssTotal)} = {n(r.etaSquared)}
        </p>
        <p>
          ω² = (SS<sub>B</sub> − (k − 1)MS<sub>W</sub>) / (SS<sub>T</sub> + MS
          <sub>W</sub>) = {n(r.omegaSquared)}
        </p>
      </Working>
      <ApaReport
        copiedLabel={strings.copied}
        copyLabel={strings.copyApa}
        heading={strings.apaHeading}
        note={t(strings.apaNote, {
          omega: fixed(r.omegaSquared, 2, fmt, false),
        })}
        parts={apa}
      />
    </section>
  );
}
